import { RunDetail } from '@lyra/shared';

/**
 * Ported verbatim from v1 `app/src/hooks/useClassification.ts` (~L131-147) —
 * same arithmetic (start/finish diff, avg-ms/row via Math.round), only the
 * field accesses are re-pointed at the `@lyra/shared` `RunDetail` shape.
 * That shape is already camelCase (`startedAt`, `finishedAt`, `processedRows`
 * — verified in `shared/src/mapari.ts`), matching what v1's own local
 * `RunDetail` (app/src/services/dataApi.ts) already used, so no field
 * renaming was needed beyond typing against the shared interface.
 */
export function deriveTiming(run: RunDetail): {
  processingTimeMs: number | null;
  averageTimePerEntry: number | null;
} {
  const startedAt = run.startedAt ? new Date(run.startedAt).getTime() : null;
  const finishedAt = run.finishedAt ? new Date(run.finishedAt).getTime() : null;
  const processingTimeMs =
    startedAt != null && finishedAt != null && finishedAt >= startedAt
      ? finishedAt - startedAt
      : null;
  const averageTimePerEntry =
    processingTimeMs != null && run.processedRows > 0
      ? Math.round(processingTimeMs / run.processedRows)
      : null;

  return { processingTimeMs, averageTimePerEntry };
}

export const THROUGHPUT_ROWS_PER_HOUR = 2500;

/**
 * Mirrors server `computeBilledHours` (server/src/services/metering.ts):
 * 1.0h minimum, then 0.1h steps rounded up. The server's version works in
 * minutes (STEP_MINUTES = 6, i.e. 0.1h) from two Date objects; this client
 * copy takes elapsed hours directly. `beyond / 0.1` here is the same ratio
 * as the server's `beyondFirstHourMinutes / 6` (both reduce to
 * `beyondHours * 10`), so the two produce identical billed-hours for the
 * same elapsed duration.
 */
function billedHoursForElapsed(elapsedHours: number): number {
  if (elapsedHours <= 1) return 1;
  const beyond = elapsedHours - 1;
  const steps = Math.ceil(beyond / 0.1 - 1e-9);
  return Math.round((1 + steps * 0.1) * 10) / 10;
}

export function estimateMapareCost(
  rows: number,
  hourlyPriceEur: number
): { billedHours: number; costEur: number } {
  const elapsedHours = rows / THROUGHPUT_ROWS_PER_HOUR;
  const billedHours = billedHoursForElapsed(elapsedHours);
  return { billedHours, costEur: Math.round(billedHours * hourlyPriceEur * 100) / 100 };
}

/**
 * Derived per-run actual cost = run duration × tier hourly rate.
 * Requires both `startedAt` and `finishedAt` to be present; a run that
 * hasn't started or finished yet (or a negative/zero duration from clock
 * skew) costs 0. Rounded to cents, same convention as `estimateMapareCost`.
 */
export function runActualCostEur(
  run: { startedAt?: string | null; finishedAt?: string | null },
  hourlyPriceEur: number
): number {
  if (!run.startedAt || !run.finishedAt) return 0;
  const durationMs = new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime();
  if (durationMs <= 0) return 0;
  const cost = (durationMs / 3_600_000) * hourlyPriceEur;
  return Math.round(cost * 100) / 100;
}
